import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import {
  countVerifyQueue,
  listVerifyQueue,
  deleteVerifyQueueItem,
  bumpVerifyAttempts,
  patchCachedVoter,
} from "@/lib/offlineDb";

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null); // { ok, failed, at }
  const syncLock = useRef(false);

  const refreshPending = useCallback(async () => {
    try {
      const n = await countVerifyQueue();
      setPending(n);
    } catch {
      /* ignore */
    }
  }, []);

  const syncNow = useCallback(async () => {
    if (syncLock.current) return;
    if (!navigator.onLine) return;
    syncLock.current = true;
    setSyncing(true);
    let ok = 0;
    let failed = 0;
    try {
      const queue = await listVerifyQueue();
      for (const item of queue) {
        try {
          await api.post(`/voters/${item.voter_id}/verify?verified=${item.verified ? "true" : "false"}`);
          await patchCachedVoter(item.voter_id, { coklit_verified: !!item.verified });
          await deleteVerifyQueueItem(item.qid);
          ok += 1;
        } catch (e) {
          // 423 (locked) or 4xx → treat as terminal so user can retry manually after admin unlocks
          const status = e?.response?.status;
          if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
            await deleteVerifyQueueItem(item.qid);
            failed += 1;
          } else {
            await bumpVerifyAttempts(item.qid);
            failed += 1;
          }
        }
      }
      setLastSyncResult({ ok, failed, at: Date.now() });
    } finally {
      await refreshPending();
      setSyncing(false);
      syncLock.current = false;
    }
    return { ok, failed };
  }, [refreshPending]);

  useEffect(() => {
    refreshPending();
    const onOnline = () => {
      setOnline(true);
      // small delay so connection stabilises before replay
      setTimeout(syncNow, 600);
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refreshPending, syncNow]);

  return (
    <OfflineContext.Provider value={{ online, pending, syncing, lastSyncResult, syncNow, refreshPending }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const ctx = useContext(OfflineContext);
  if (!ctx) {
    // safe defaults outside provider — pages can still call without crashing
    return { online: true, pending: 0, syncing: false, lastSyncResult: null, syncNow: async () => {}, refreshPending: async () => {} };
  }
  return ctx;
}
